/**
 * Ripitoo Platform Master Client Entry & Selective Device Dispatcher
 * 
 * Classifies physical client device at runtime and dynamically imports ONLY
 * the appropriate presentation layer:
 * - Mobile: experiences/mobile/mobile-app.js (mobile presentation bundle & CSS)
 * - Desktop: experiences/desktop/desktop-app.js (desktop presentation bundle & CSS)
 * 
 * Shared tool engine, math/crypto algorithms, and file processors remain shared.
 */

import './styles/bootstrap-custom.scss';
import './styles/bootstrap-icons-subset.css';
import './styles/brand-tokens.css';
import './styles/theme.css';
import './styles/components.css';

// Defer Bootstrap JS bundle to idle so it does not block initial paint and hydration
if (typeof window !== 'undefined') {
  const loadBootstrap = () => import('bootstrap/dist/js/bootstrap.bundle.min.js');
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => loadBootstrap(), { timeout: 2000 });
  } else {
    setTimeout(loadBootstrap, 1200);
  }
}

import { detectDevice } from './core/device.js';
import { Storage } from './core/storage.js';
import { pwaManager } from './core/pwa-install.js';

// Enforce canonical origin (redirect www.ripitoo.com to https://ripitoo.com for seamless OAuth and session persistence)
if (typeof window !== 'undefined' && window.location.hostname === 'www.ripitoo.com') {
  window.location.replace(`https://ripitoo.com${window.location.pathname}${window.location.search}${window.location.hash}`);
}

// Apply saved theme immediately
export function applyActiveTheme() {
  if (typeof document === 'undefined') return;
  const savedTheme = Storage.getTheme();
  const effectiveTheme = savedTheme === 'auto'
    ? (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : savedTheme;
  document.documentElement.setAttribute('data-bs-theme', effectiveTheme);

  const syncIcons = () => {
    const isDark = effectiveTheme === 'dark';
    document.querySelectorAll('#ot-desk-theme-icon').forEach(icon => {
      icon.className = isDark ? 'bi bi-sun-fill text-warning' : 'bi bi-moon-stars-fill text-body';
    });
    document.querySelectorAll('#ot-desk-theme-btn').forEach(btn => {
      btn.setAttribute('aria-label', isDark ? 'Switch to light mode' : 'Switch to dark mode');
      btn.setAttribute('title', isDark ? 'Switch to light mode' : 'Switch to dark mode');
    });
    document.querySelectorAll('#ot-mob-theme-icon').forEach(icon => {
      icon.className = isDark ? 'bi bi-sun-fill text-warning' : 'bi bi-moon-stars';
    });
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncIcons, { once: true });
    } else {
      syncIcons();
    }
  }
}

applyActiveTheme();

// Universal 1-Click Theme Switcher Event Delegation (Instant response on Desktop & Mobile)
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    const toggleBtn = e.target.closest('#ot-desk-theme-btn, #ot-mob-theme-toggle, .ot-theme-toggle-btn, .ot-desktop-theme-toggle');
    if (!toggleBtn) return;
    e.preventDefault();

    const nextTheme = Storage.toggleTheme();
    document.documentElement.setAttribute('data-bs-theme', nextTheme);

    const isDark = nextTheme === 'dark';
    document.querySelectorAll('#ot-desk-theme-icon').forEach(icon => {
      icon.className = isDark ? 'bi bi-sun-fill text-warning' : 'bi bi-moon-stars-fill text-body';
    });
    document.querySelectorAll('#ot-desk-theme-btn, .ot-desktop-theme-toggle').forEach(btn => {
      btn.setAttribute('aria-label', isDark ? 'Switch to light mode' : 'Switch to dark mode');
      btn.setAttribute('title', isDark ? 'Switch to light mode' : 'Switch to dark mode');
    });
    document.querySelectorAll('#ot-mob-theme-icon').forEach(icon => {
      icon.className = isDark ? 'bi bi-sun-fill text-warning' : 'bi bi-moon-stars';
    });
  });
}

// Dynamically react to system OS dark/light mode preference changes
if (typeof window !== 'undefined' && window.matchMedia) {
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (Storage.getTheme() === 'auto') {
        applyActiveTheme();
      }
    });
  } catch {}
}

const initClientApp = async () => {
  // Initialize PWA installation and standalone mode manager
  try {
    pwaManager.init();
  } catch (err) {
    console.warn('[PWA] Init notice:', err);
  }

  // Initialize Google Consent Mode v2 and GA4 tracking
  try {
    const { initAnalytics } = await import('./core/analytics.js');
    initAnalytics();
  } catch (err) {
    console.warn('[Analytics] Init notice:', err);
  }

  // Initialize Google Consent Mode v2 Banner & Preferences Modal
  try {
    const { initConsentBanner } = await import('./components/ConsentBanner.js');
    initConsentBanner();
  } catch (err) {
    console.warn('[Consent] Init notice:', err);
  }

  // Initialize Ads with consent state awareness
  try {
    const { initAds } = await import('./core/ads.js');
    initAds();
  } catch (err) {
    console.warn('[Ads] Init notice:', err);
  }

  // Device Classification & Selective Presentation Loading
  const device = detectDevice();

  if (device === 'mobile') {
    try {
      const { initMobileExperience } = await import('./experiences/mobile/mobile-app.js');
      initMobileExperience();
    } catch (err) {
      console.error('[Ripitoo Mobile] Error loading mobile presentation:', err);
      const { router } = await import('./core/router.js');
      router.init();
    }
  } else {
    try {
      const { initDesktopExperience } = await import('./experiences/desktop/desktop-app.js');
      initDesktopExperience();
    } catch (err) {
      console.error('[Ripitoo Desktop] Error loading desktop presentation:', err);
      const { router } = await import('./core/router.js');
      router.init();
    }
  }

  // Register offline Progressive Web App (PWA) Service Worker
  if ('serviceWorker' in navigator && window.location.protocol.startsWith('http')) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((err) => {
        console.debug('[SW] Registration notice:', err.message);
      });
    });
  }
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initClientApp, { once: true });
  } else {
    initClientApp();
  }
}
